import React, { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Box,
  FormControl,
  Grid,
  IconButton,
  Input,
  makeStyles,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { TableContainer } from '@mui/material'
import { nanoid } from '@reduxjs/toolkit'
import { useTranslation } from 'react-i18next'
import NumberFormat from 'react-number-format'
import LoadingBox from '../form/LoadingBox'

const useStyles = makeStyles(theme => ({
  summaryLabel: {
    backgroundColor: theme.colors.proposalColors.summary_bg,
    fontSize: '12px',
    height: '22px',
    paddingTop: '3px',
    paddingBottom: '3px',
    color: theme.colors.proposalColors.dark_grey,
    fontWeight: '500',
    borderBottom: '0px'
  },
  bottomSummary: {
    paddingLeft: '16px',
    display: 'flex',
    alignItems: 'center'
  },
  tableHeader: {
    fontSize: '12px',
    fontWeight: '400',
    color: theme.colors.proposalColors.table_text,
    paddingTop: '5px',
    paddingBottom: '5px',
    borderBottom: '1px solid ' + theme.colors.proposalColors.borders
  },
  centerHeader: {
    fontSize: '12px',
    fontWeight: '400',
    color: theme.colors.proposalColors.table_text,
    paddingTop: '5px',
    paddingBottom: '5px',
    borderBottom: '1px solid ' + theme.colors.proposalColors.borders,
    textAlign: 'right'
  },
  tableRow: {
    height: '54px'
  },
  tableItem: {
    fontSize: '12px',
    color: theme.colors.proposalColors.table_text,
    borderBottom: '1px solid ' + theme.colors.proposalColors.borders
  },
  tableRemoveItem: {
    fontSize: '12px',
    width: '12px',
    color: theme.colors.proposalColors.table_text,
    borderBottom: '1px solid ' + theme.colors.proposalColors.borders
  },
  tableDescItem: {
    fontSize: '12px',
    width: '52%',
    color: theme.colors.proposalColors.table_text,
    borderBottom: '1px solid ' + theme.colors.proposalColors.borders
  },
  deleteRow: {
    color: theme.colors.proposalColors.disabled_grey,
    size: '12px'
  },
  selectItem: {
    fontSize: '12px',
    minWidth: props => (props.pricingEngine ? '100px' : '130px'),
    maxWidth: props => (props.pricingEngine ? '100px' : '130px'),
    borderBottom: '1px solid ' + theme.colors.proposalColors.borders
  },
  innerSelectItem: {
    fontSize: '12px',
    width: '140px'
  },
  selectItemButton: {
    fontSize: '12px',
    backgroundColor: theme.colors.proposalColors.buttons.blue_button,
    color: 'white',
    '&.Mui-disabled': {
      color: theme.colors.proposalColors.disabled_grey,
      backgroundColor: 'white'
    }
  },
  descriptionItem: {
    fontSize: '12px',
    width: '100%',
    paddingLeft: '0px',
    '& input': {
      color: theme.colors.proposalColors.blue_text + ' !important'
    },
    '& textarea': {
      color: theme.colors.proposalColors.blue_text + ' !important'
    }
  },
  inputNumericItem: {
    fontSize: '12px',
    minWidth: '70px',
    textAlign: 'right',
    color: theme.colors.proposalColors.blue_text + ' !important'
  },
  blackItem: {
    fontSize: '12px',
    minWidth: '70px',
    textAlign: 'center'
  },
  taxLabel: {
    margin: 'auto',
    textAlign: 'left',
    color: theme.colors.proposalColors.grey_text
  },
  inputQtyItem: {
    fontSize: '12px',
    minWidth: '50px',
    textAlign: 'right',
    color: theme.colors.proposalColors.blue_text + ' !important'
  },
  inputAmountItem: {
    fontSize: '12px',
    minWidth: '95px',
    textAlign: 'right'
  },
  searchIcon: {
    paddingRight: '5px'
  },
  addButton: {
    color: theme.colors.proposalColors.buttons.blue_button,
    backgroundColor: 'white',
    marginTop: '17px',
    marginBottom: '17px',
    fontSize: '15px',
    '&.Mui-disabled': {
      color: theme.colors.proposalColors.disabled_grey,
      backgroundColor: 'white'
    }
  },
  addText: {
    color: theme.colors.proposalColors.dark_grey,
    fontSize: '12px'
  },
  select: { fontSize: '12px', width: '120px' },
  blueColor: {
    '& .MuiSelect-root': {
      color: `${theme.colors.iconBlue} !important`
    }
  },
  dialogMessage: {
    fontSize: '12px',
    fontWeight: '700',
    textAlign: 'left'
  },
  dialogWrapper: {
    width: '500px',
    padding: '20px'
  },
  serviceAndWeight: {
    display: 'flex',
    gap: '20px'
  },
  modalRow: {
    display: 'flex',
    flexDirection: 'column'
  },
  modalLabel: {
    fontSize: '12px',
    fontWeight: '700',
    minWidth: '120px',
    textAlign: 'left'
  },
  modalInput: {
    flex: 1,
    width: '100%'
  },
  weightInputs: {
    display: 'flex',
    gap: '8px'
  },
  weightInput: {
    width: '120px'
  },
  editIcon: {
    color: theme.colors.blue,
    fontSize: '14px'
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  modalField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  serviceField: {
    flex: 1
  },
  weightField: {
    flex: 1
  },
  acceptButton: {
    '&.MuiButton-root.Mui-disabled': {
      backgroundColor: theme.colors.grey_2,
      color: theme.colors.text_grey
    }
  },
  refrigerantButtonsContainer: {
    padding: '0px 35px'
  },
  noBottomBorder: {
    borderBottom: 'none'
  },
  summaryValueLabel: {
    fontSize: '12px',
    lineHeight: '14px',
    fontWeight: '400',
    color: theme.colors.proposalColors.dark_text,
    padding: '15px 0px'
  },
  summaryTotal: {
    fontSize: '18px',
    lineHeight: '27px',
    fontWeight: '700',
    color: theme.colors.proposalColors.grey_text,
    padding: '15px 0px 25px'
  },
  summaryValue: {
    fontSize: '18px',
    lineHeight: '27px',
    fontWeight: '700',
    color: theme.colors.proposalColors.black_text,
    borderBottom: 'none',
    padding: '15px 0px 25px'
  },
  summaryBox: {
    display: 'flex',
    marginRight: '50px'
  },
  summaryBox1: {
    display: 'flex',
    marginRight: '65px'
  },
  loadingBox_1: {
    width: '100%',
    height: '20px'
  },
  loadingBox_2: {
    width: '20%',
    height: '20px',
    marginRight: '20px 0px'
  },
  icon_cancel: {
    fontSize: '12px'
  }
}))

function createData(
  id,
  item,
  description,
  tax,
  rate,
  quantity,
  amount,
  factor
) {
  return {
    id,
    item,
    description,
    tax,
    rate,
    quantity,
    amount,
    factor
  }
}

function CustomSelect(props) {
  const classes = useStyles()
  const { items, itemTypes, rowId, value, disabled } = props
  const [itemSelected, setItemSelected] = useState(value)
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState(null)

  const handleSelect = event => {
    setItemSelected(event.target.value)
    if (props.onSelectChange) {
      props.onSelectChange(rowId, 'item', event.target.value)
    }
  }

  useEffect(() => {
    setItemSelected(value)
  }, [value, items])

  return (
    <FormControl>
      <Select
        id="select-small"
        onChange={handleSelect}
        className={`${classes.select} ${
          itemSelected?.toString() === '0' ? classes.blueColor : null
        }`}
        value={itemSelected}
        disabled={disabled}
        onOpen={event => {
          setAnchorEl(event.currentTarget)
        }}
        onClose={() => setAnchorEl(null)}
        disableUnderline
        IconComponent={() => <></>}
        MenuProps={{
          anchorEl: anchorEl,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
          },
          getContentAnchorEl: null
        }}
      >
        <MenuItem
          value="0"
          disabled
          classes={{ root: classes.innerSelectItem }}
          style={anchorEl ? { display: 'none' } : null}
        >
          {t('proposals.placeholder.select_one')}
        </MenuItem>
        {/* dynamic items from SF */}
        {itemTypes &&
          itemTypes.length > 0 &&
          itemTypes.map(itemType => (
            <MenuItem
              key={itemType}
              value={itemType}
              classes={{ root: classes.innerSelectItem }}
            >
              {itemType}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  )
}

function EnhancedTable(props) {
  const {
    updateItems,
    items,
    itemTypes,
    pricingEngine,
    status,
    loading,
    disableButtons,
    editionDisabled,
    handleUpdateItems,
    handleRemoveItem,
    subtotal,
    taxSum,
    totalAmount
  } = props
  const classes = useStyles({ pricingEngine })
  const { t } = useTranslation()
  const [disableAddItem, setDisableAddItem] = useState(false)
  const rows = [createData(nanoid(), '0', '', 0, 0, 0, 0, 0, 1)]
  useEffect(() => {
    if (!items?.length > 0) {
      updateItems(rows)
    }
    if (editionDisabled) {
      setDisableAddItem(true)
    }
    if (items?.length === 1) {
      items[0].amount !== 0 ? setDisableAddItem(false) : setDisableAddItem(true)
    }
  }, [items, status])

  let rawValue = 0

  const columns = [
    {
      field: 'item',
      headerName: t('proposals.column_names.item')
    },
    {
      field: 'description',
      headerName: t('proposals.column_names.item_description')
    },
    {
      field: 'quantity',
      headerName: t('proposals.column_names.quantity')
    },
    {
      field: 'tax',
      headerName: t('proposals.column_names.tax')
    },
    {
      field: 'rate',
      headerName: t('proposals.column_names.rate')
    },
    {
      field: 'amount',
      headerName: t('proposals.column_names.amount')
    },
    {
      field: 'delete',
      headerName: ''
    }
  ]

  const handleNumberChange = (event, rowId) => {
    const fieldName = event.target.getAttribute('name')
    handleUpdateItems(rowId, fieldName, rawValue)
  }

  const getTaxValue = row => {
    const quantity = row.quantity || 0
    const rate = row.rate || 0
    const taxPercentage = row.tax || 0
    const taxAmount = (quantity * rate * taxPercentage) / 100
    return taxAmount
  }

  const formatCurrency = value => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value)
  }
  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell colSpan={7} classes={{ root: classes.summaryLabel }}>
              {loading ? '' : t('proposals.labels.product_or_service')}
            </TableCell>
          </TableRow>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell
                key={column.field + index}
                classes={{
                  root: [2, 3, 4, 5].includes(index)
                    ? classes.centerHeader
                    : classes.tableHeader
                }}
              >
                {loading ? (
                  <LoadingBox className={classes.loadingBox_1} />
                ) : (
                  column.headerName
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items?.map((row, index) => (
            <TableRow
              key={row.id + '-' + index}
              classes={{ root: classes.tableRow }}
            >
              <TableCell
                classes={{
                  root: classes.selectItem
                }}
              >
                {itemTypes &&
                (itemTypes.includes(row.item) || row?.item === '0') &&
                !editionDisabled ? (
                  <CustomSelect
                    items={items}
                    itemTypes={itemTypes}
                    rowId={row.id}
                    value={row.item}
                    disabled={editionDisabled}
                    onSelectChange={handleUpdateItems}
                  ></CustomSelect>
                ) : (
                  <Typography classes={{ root: classes.tableDescItem }}>
                    {row.item}
                  </Typography>
                )}
              </TableCell>
              <TableCell classes={{ root: classes.tableDescItem }}>
                <Box display="flex" alignItems="center">
                  <Input
                    classes={{ root: classes.descriptionItem }}
                    placeholder={t('proposals.placeholder.item_desc')}
                    name="description"
                    multiline
                    maxRows={3}
                    onChange={event =>
                      handleUpdateItems(
                        row.id,
                        'description',
                        event.target.value
                      )
                    }
                    onFocus={event => event.target.select()}
                    disableUnderline
                    value={row.description}
                    disabled={editionDisabled}
                  />
                </Box>
              </TableCell>
              <TableCell classes={{ root: classes.tableItem }}>
                <NumberFormat
                  name="quantity"
                  thousandSeparator
                  allowNegative={false}
                  customInput={Input}
                  disableUnderline
                  value={row.quantity}
                  onValueChange={values => (rawValue = values.value)}
                  onChange={event => handleNumberChange(event, row.id)}
                  onFocus={event => event.target.select()}
                  classes={{
                    input: classes.inputQtyItem
                  }}
                  disabled={editionDisabled}
                  inputProps={{ maxLength: 4 }}
                />
              </TableCell>
              <TableCell classes={{ root: classes.tableItem }}>
                <Box display="flex" flexDirection={'row'}>
                  <NumberFormat
                    name="tax"
                    thousandSeparator
                    customInput={Input}
                    disableUnderline
                    suffix="%"
                    allowEmptyFormatting
                    decimalScale={2}
                    decimalSeparator="."
                    allowNegative={false}
                    value={row.tax}
                    onValueChange={values => (rawValue = values.value)}
                    onChange={event => handleNumberChange(event, row.id)}
                    onFocus={event => event.target.select()}
                    classes={{
                      input: classes.inputNumericItem
                    }}
                    disabled={editionDisabled}
                    inputProps={{ maxLength: 11 }}
                  />
                  <Typography
                    variant="body2"
                    className={classes.taxLabel + ' ' + classes.blackItem}
                  >
                    (
                    {getTaxValue(row).toLocaleString(undefined, {
                      style: 'currency',
                      currency: 'USD'
                    })}
                    )
                  </Typography>
                </Box>
              </TableCell>
              <TableCell classes={{ root: classes.tableItem }}>
                <NumberFormat
                  name="rate"
                  thousandSeparator
                  customInput={Input}
                  disableUnderline
                  prefix="$"
                  allowEmptyFormatting
                  decimalScale={2}
                  decimalSeparator="."
                  allowNegative={false}
                  value={row.rate}
                  onValueChange={values => (rawValue = values.value)}
                  onChange={event => handleNumberChange(event, row.id)}
                  onFocus={event => event.target.select()}
                  classes={{
                    input: classes.inputNumericItem
                  }}
                  disabled={editionDisabled}
                  inputProps={{ maxLength: 11 }}
                />
              </TableCell>
              <TableCell classes={{ root: classes.tableItem }}>
                <NumberFormat
                  name="amount"
                  thousandSeparator
                  customInput={Input}
                  disableUnderline
                  prefix="$"
                  decimalScale={2}
                  fixedDecimalScale={2}
                  value={row.amount}
                  disabled
                  classes={{
                    input: classes.inputAmountItem
                  }}
                />
              </TableCell>
              <TableCell classes={{ root: classes.tableRemoveItem }}>
                {!editionDisabled && (
                  <IconButton
                    onClick={event => handleRemoveItem(event, row.id)}
                    size="small"
                  >
                    <FontAwesomeIcon
                      icon="fa-regular fa-circle-xmark"
                      className={classes.icon_cancel}
                    />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {loading ? (
        <LoadingBox className={classes.loadingBox_2} />
      ) : (
        !editionDisabled && (
          <Grid container alignItems="center">
            <Grid item>
              <IconButton
                onClick={props.handleAddItem}
                classes={{ root: classes.addButton }}
                disabled={
                  (disableAddItem || disableButtons) && !items?.length === 0
                }
                size="small"
              >
                <FontAwesomeIcon
                  icon="fa-solid fa-circle-plus"
                  classes={{ root: classes.addButton }}
                />
              </IconButton>
            </Grid>
            <Grid item>
              <Box classes={{ root: classes.addText }}>
                {t('proposals.actions.add_item')}
              </Box>
            </Grid>
          </Grid>
        )
      )}
      <Box>
        <Box
          className={editionDisabled ? classes.summaryBox : classes.summaryBox1}
        >
          <Box flex="2" />
          <Box flex="1">
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell classes={{ root: classes.summaryValueLabel }}>
                    {t('proposals.summary.subtotal') + ': '}
                  </TableCell>
                  <TableCell
                    align="right"
                    classes={{ root: classes.summaryValueLabel }}
                  >
                    {formatCurrency(subtotal)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell classes={{ root: classes.summaryValueLabel }}>
                    {t('proposals.summary.tax') + ': '}
                  </TableCell>
                  <TableCell
                    align="right"
                    classes={{ root: classes.summaryValueLabel }}
                  >
                    {formatCurrency(taxSum)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    classes={{
                      root: `${classes.summaryTotal} ${classes.noBottomBorder}`
                    }}
                  >
                    {t('proposals.summary.total')}
                  </TableCell>
                  <TableCell
                    align="right"
                    classes={{ root: classes.summaryValue }}
                  >
                    {formatCurrency(totalAmount)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Box>
      </Box>
    </TableContainer>
  )
}

export const ProposalItems = props => {
  const {
    updateItems,
    items,
    itemTypes,
    pricingEngine,
    status,
    loading,
    disableButtons,
    editionDisabled,
    handleUpdateItems,
    handleAddItem,
    handleRemoveItem,
    subtotal,
    taxSum,
    totalAmount
  } = props
  return (
    <EnhancedTable
      updateItems={updateItems}
      items={items}
      itemTypes={itemTypes}
      pricingEngine={pricingEngine}
      status={status}
      loading={loading}
      disableButtons={disableButtons}
      editionDisabled={editionDisabled}
      handleUpdateItems={handleUpdateItems}
      handleAddItem={handleAddItem}
      handleRemoveItem={handleRemoveItem}
      subtotal={subtotal}
      taxSum={taxSum}
      totalAmount={totalAmount}
    />
  )
}
